import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  InputAdornment,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Search, Close } from '@mui/icons-material';
import { getApiData } from '../../utils/APIHelper';
import { Setting } from '../../utils/Setting';

const ExpertiseSelectionModal = ({ 
  open, 
  onClose, 
  onSave, 
  initialSelected = [], 
  userRole = 'contractor' 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expertiseList, setExpertiseList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [selectedExpertise, setSelectedExpertise] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      fetchExpertiseList();
      setSelectedExpertise(initialSelected);
    }
  }, [open, initialSelected]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = expertiseList.filter(item =>
        item.project_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredList(filtered);
    } else {
      setFilteredList(expertiseList);
    }
  }, [searchTerm, expertiseList]);

  const fetchExpertiseList = async () => {
    setLoading(true);
    try {
      const response = await getApiData(
        `${Setting.endpoints.expertiseList}`,
        "GET",
        {}
      );
      if (response.success) {
        const matched = response.data.filter(
          (item) => item.type === userRole
        );
        const finalList = matched.length ? matched : response.data;
        setExpertiseList(finalList);
        setFilteredList(finalList);
      }
    } catch (error) {
      console.log("🚀 ~ Error fetching expertise list:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExpertiseToggle = (expertise) => {
    setSelectedExpertise(prev => {
      const isSelected = prev.some(item => item.id === expertise.id);
      if (isSelected) {
        return prev.filter(item => item.id !== expertise.id);
      } else {
        return [...prev, expertise];
      }
    });
  };

  const handleRemoveSelected = (expertiseId) => {
    setSelectedExpertise(prev => prev.filter(item => item.id !== expertiseId));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Extract IDs from selected expertise
      const expertiseIds = selectedExpertise.map(item => item.id);
      onSave(expertiseIds, selectedExpertise);
      onClose();
    } catch (error) {
      console.log("🚀 ~ Error saving expertise:", error);
    } finally {
      setSaving(false);
    }
  };

  const isSelected = (expertise) => {
    return selectedExpertise.some(item => item.id === expertise.id);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // A 600px minHeight fights MUI's own `calc(100% - 64px)` max on a short
      // phone viewport, squeezing the list. Go full screen there instead.
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: 3 },
          minHeight: { xs: '100%', md: '600px' },
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        pb: 1
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '17px', md: '20px' }, letterSpacing: 0 }}>
          Select Areas of Expertise
        </Typography>
        <Button
          onClick={onClose}
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
        {/* Search Field */}
        <TextField
          fullWidth
          placeholder="Search expertise areas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />

        {/* Selected Expertise */}
        {selectedExpertise.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
              Selected ({selectedExpertise.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedExpertise.map((expertise) => (
                <Chip
                  key={expertise.id}
                  label={expertise.project_name}
                  onDelete={() => handleRemoveSelected(expertise.id)}
                  sx={{
                    backgroundColor: '#E3F2FD',
                    color: '#1976D2',
                    '& .MuiChip-deleteIcon': {
                      color: '#1976D2',
                    }
                  }}
                />
              ))}
            </Box>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {/* Expertise List */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
            Available Expertise Areas
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {filteredList.map((expertise) => (
                <ListItem
                  key={expertise.id}
                  disablePadding
                  sx={{
                    border: '1px solid #E5E7EB',
                    borderRadius: 2,
                    mb: 1,
                    backgroundColor: isSelected(expertise) ? '#F3F4F6' : 'white',
                  }}
                >
                  <ListItemButton
                    onClick={() => handleExpertiseToggle(expertise)}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <Checkbox
                      checked={isSelected(expertise)}
                      sx={{ mr: 2 }}
                    />
                    <ListItemText
                      primary={expertise.project_name}
                      primaryTypographyProps={{
                        fontWeight: isSelected(expertise) ? 600 : 400,
                        color: isSelected(expertise) ? '#1F2937' : '#6B7280',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              
              {filteredList.length === 0 && !loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                    {searchTerm ? 'No expertise areas found matching your search.' : 'No expertise areas available.'}
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || selectedExpertise.length === 0}
          sx={{
            backgroundColor: '#4D6BF6',
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            '&:hover': {
              backgroundColor: '#3B5BDB',
            }
          }}
        >
          {saving ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              Saving...
            </>
          ) : (
            `Save ${selectedExpertise.length} expertise area${selectedExpertise.length !== 1 ? 's' : ''}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpertiseSelectionModal;
